// Must come first: @react-pdf/renderer needs the Node `Buffer` global at module
// scope, and without it invoice PDFs generate with the logo missing.
import './lib/buffer-polyfill'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import './index.css'
import './styles/theme.css'
import App from './App.tsx'

import { ThemeProvider } from './context/ThemeContext.tsx'
import { ConfirmProvider } from './components/ui/ConfirmProvider.tsx'
import { AppToaster } from './components/ui/AppToaster.tsx'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
})

function FallbackUI() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--fd-base)', color: 'var(--fd-text-bright)', gap: '12px' }}>
      <p style={{ fontSize: '1rem', color: 'var(--fd-text-soft)' }}>Something went wrong. Our team has been notified.</p>
      <button onClick={() => window.location.reload()} style={{ padding: '8px 20px', background: 'var(--fd-btn-face)', color: 'var(--fd-btn-ink)', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
        Reload
      </button>
    </div>
  )
}

// Re-arm the stale-shell self-heal (the flag is set by the capture-phase
// handler in index.html) once the app has not just booted but *stayed* up.
//
// The delay is the important part. Clearing it the instant the bundle parses
// lets a flaky network re-fire the recovery immediately — fail, reload, clear,
// fail again — which is a reload loop wearing a different hat. Requiring a
// quiet period means each recovery must be followed by a genuinely working
// app, while still re-arming for the next deploy (an installed PWA can hold
// one tab session open across several).
setTimeout(() => {
    try { sessionStorage.removeItem('fds_sw_recovered'); } catch { /* private mode */ }
}, 15_000);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<FallbackUI />} showDialog>
      <BrowserRouter>
        <ThemeProvider>
          {/* One Toaster for the whole app. There were four, all of them inside
              ControlTower / Blog components, so every toast.success() and
              toast.error() in the DASHBOARD rendered nothing at all — the
              expenses review queue, the returns bay, the ledger and the manual
              expense modal were all reporting success and failure into a void.
              react-hot-toast needs exactly one mounted Toaster; more than one
              renders the same toast repeatedly. */}
          <AppToaster />
          <ConfirmProvider>
            <App />
          </ConfirmProvider>
        </ThemeProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
